import Alpine from "alpinejs";
import "../styles/main.scss";

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const normalizeFocalPoint = (value) => {
  const raw = String(value || "").trim();
  if (!raw) {
    return "50% 50%";
  }

  // Keep focal-point styles restricted to CSS percent pairs.
  return /^[0-9.\-]+%\s+[0-9.\-]+%$/.test(raw) ? raw : "50% 50%";
};

const debounce = (fn, wait = 180) => {
  let timeoutId = null;
  return (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => fn(...args), wait);
  };
};

const MOBILE_BREAKPOINT = 900;

window.siteChrome = () => ({
  navActive: false,
  searchActive: false,

  init() {
    this.navActive = document.body.classList.contains("nav-active");
    this.searchActive = document.body.classList.contains("search-active");
    this.applyBodyClasses();
    window.addEventListener("search:request-close", () => this.closeSearch());
  },

  applyBodyClasses() {
    document.body.classList.toggle("nav-active", this.navActive);
    document.body.classList.toggle("search-active", this.searchActive);
  },

  toggleNav() {
    if (this.searchActive) {
      this.searchActive = false;
      this.navActive = false;
    } else {
      this.navActive = !this.navActive;
    }

    this.applyBodyClasses();
  },

  openSearch() {
    this.searchActive = true;
    this.navActive = false;
    this.applyBodyClasses();
    window.dispatchEvent(new CustomEvent("search:open"));
  },

  closeSearch() {
    this.searchActive = false;
    document.body.classList.remove("search-has-suggestions", "search-show-results");
    this.applyBodyClasses();
    window.dispatchEvent(new CustomEvent("search:close"));
  },
});

window.searchAutocomplete = () => ({
  endpoint: "/actions/span-search/query/suggest",
  resultsEndpoint: "/actions/span-search/query",
  query: "",
  suggestions: [],
  hasMore: false,
  activeIndex: -1,
  visible: false,
  resultsVisible: false,
  controller: null,
  resultsController: null,
  debouncedLookup: null,
  panelHeightTimeoutId: null,

  getSuggestionLimit() {
    return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches ? 6 : 12;
  },

  init() {
    this.query = this.$refs.searchInput?.value?.trim() || "";
    this.debouncedLookup = debounce((query) => this.lookup(query), 180);

    window.addEventListener("search:open", () => {
      this.$nextTick(() => {
        this.$refs.searchInput?.focus();
        this.schedulePanelHeightUpdate();
      });
    });

    window.addEventListener("search:close", () => {
      this.query = "";
      this.resultsVisible = false;
      this.clear();
      this.renderResults("<div class='search-loading'>Start typing to search.</div>");
      document.body.style.removeProperty("--search-panel-height");
    });

    window.addEventListener("resize", () => this.schedulePanelHeightUpdate());
  },

  clear() {
    this.suggestions = [];
    this.hasMore = false;
    this.activeIndex = -1;
    this.visible = false;
    this.applyStageClasses();
    this.schedulePanelHeightUpdate();
  },

  select(url) {
    const target = String(url || "").trim();
    if (!target) {
      return;
    }

    window.location.href = target;
  },

  onInput() {
    const value = this.query.trim();
    this.resultsVisible = false;
    document.body.classList.remove("search-show-results");
    if (value.length < 2) {
      this.clear();
      this.renderResults("<div class='search-loading'>Start typing to search.</div>");
      this.schedulePanelHeightUpdate();
      return;
    }

    this.debouncedLookup(value);
  },

  async lookup(value) {
    if (this.controller) {
      this.controller.abort();
    }

    this.controller = new AbortController();

    try {
      const url = new URL(this.endpoint, window.location.origin);
      url.searchParams.set("q", value);

      const response = await fetch(url.toString(), { signal: this.controller.signal });
      if (!response.ok) {
        throw new Error("Autocomplete request failed");
      }

      const data = await response.json();
      const nextSuggestions = Array.isArray(data.suggestions) ? data.suggestions : [];
      const suggestionLimit = this.getSuggestionLimit();
      this.hasMore = nextSuggestions.length > suggestionLimit;
      this.suggestions = nextSuggestions.slice(0, suggestionLimit);
      this.activeIndex = -1;
      this.visible = this.suggestions.length > 0;
      this.applyStageClasses();
      this.bindSuggestionImageHeightUpdates();
      this.schedulePanelHeightUpdate();
    } catch (error) {
      if (error.name !== "AbortError") {
        this.clear();
      }
    }
  },

  onKeydown(event) {
    if (!this.visible || !this.suggestions.length) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      this.activeIndex = Math.min(this.activeIndex + 1, this.suggestions.length - 1);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      this.activeIndex = Math.max(this.activeIndex - 1, 0);
      return;
    }

    if (event.key === "Escape") {
      window.dispatchEvent(new CustomEvent("search:close"));
      this.clear();
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      if (this.activeIndex >= 0 && this.suggestions[this.activeIndex]) {
        this.select(this.suggestions[this.activeIndex].url);
        return;
      }
      this.openResults();
      return;
    }

    this.applyStageClasses();
  },

  openResults() {
    const value = this.query.trim();
    if (value.length < 2) {
      return;
    }

    const url = new URL("/search", window.location.origin);
    url.searchParams.set("q", value);
    window.location.href = url.toString();
  },

  renderResults(html) {
    const target = document.getElementById("global-search-results-app");
    if (target) {
      target.innerHTML = html;
    }
  },

  applyStageClasses() {
    const shouldExpandSuggestions = document.body.classList.contains("search-active") && this.visible && !this.resultsVisible;
    document.body.classList.toggle("search-has-suggestions", shouldExpandSuggestions);
    document.body.classList.toggle("search-show-results", document.body.classList.contains("search-active") && this.resultsVisible);
    if (!this.resultsVisible) {
      this.schedulePanelHeightUpdate();
    }
  },

  schedulePanelHeightUpdate() {
    this.$nextTick(() => {
      this.updatePanelHeight();
      window.requestAnimationFrame(() => this.updatePanelHeight());

      if (this.panelHeightTimeoutId) {
        window.clearTimeout(this.panelHeightTimeoutId);
      }

      this.panelHeightTimeoutId = window.setTimeout(() => {
        this.updatePanelHeight();
      }, 120);
    });
  },

  bindSuggestionImageHeightUpdates() {
    const panel = document.querySelector(".search-container");
    const images = panel?.querySelectorAll(".search-autocomplete--global img.thumbnail");
    if (!images || !images.length) {
      return;
    }

    images.forEach((img) => {
      if (img.dataset.heightBound === "1") {
        return;
      }

      img.dataset.heightBound = "1";

      if (img.complete) {
        this.schedulePanelHeightUpdate();
        return;
      }

      img.addEventListener("load", () => this.schedulePanelHeightUpdate(), { once: true });
      img.addEventListener("error", () => this.schedulePanelHeightUpdate(), { once: true });
    });
  },

  updatePanelHeight() {
    if (!document.body.classList.contains("search-active") || this.resultsVisible) {
      return;
    }

    const panel = document.querySelector(".search-container");
    const inner = panel?.querySelector(".search-container__inner");
    if (!panel || !inner) {
      return;
    }

    const measured = Math.ceil(inner.getBoundingClientRect().height);
    const safeHeight = Math.min(measured, window.innerHeight);
    document.body.style.setProperty("--search-panel-height", `${safeHeight}px`);
  },
});

const activePlyrPlayers = [];
let plyrConstructorPromise = null;

const loadPlyrConstructor = async () => {
  if (!plyrConstructorPromise) {
    plyrConstructorPromise = import("plyr")
      .then(({ default: Plyr }) => Plyr)
      .catch(() => null);
  }

  return plyrConstructorPromise;
};

const registerPlyrPlayer = (player) => {
  const shell = player.elements?.container?.closest(".progressive-video");
  if (shell) {
    player.on("ready", () => {
      shell.classList.add("is-loaded");
    });
  }

  player.on("play", () => {
    if (shell) {
      shell.classList.add("is-loaded", "has-played");
    }

    activePlyrPlayers.forEach((other) => {
      if (other !== player) {
        other.pause();
      }
    });
  });

  activePlyrPlayers.push(player);
};

const setupPlyrNodes = async (nodes) => {
  const playerNodes = Array.from(nodes).filter((node) => node.dataset.plyrInitialized !== "1");
  if (!playerNodes.length) {
    return;
  }

  const Plyr = await loadPlyrConstructor();
  if (!Plyr) {
    return;
  }

  playerNodes.forEach((node) => {
    node.dataset.plyrInitialized = "1";
    registerPlyrPlayer(new Plyr(node));
  });
};

const initPlayers = async (root = document) => {
  await setupPlyrNodes(root.querySelectorAll(".player:not([data-lazy-player])"));
};

const LAZY_PLAYER_ROOT_MARGIN = "1200px 0px";
const LAZY_NATIVE_VIDEO_ROOT_MARGIN = "200px 0px";

const loadNativeVideo = (video) => {
  if (!(video instanceof HTMLVideoElement) || video.dataset.lazyVideoLoaded === "1") {
    return;
  }

  video.dataset.lazyVideoLoaded = "1";
  if (video.dataset.poster && video.poster !== video.dataset.poster) {
    video.poster = video.dataset.poster;
  }
  video.preload = video.dataset.lazyPreload || "metadata";

  if (video.dataset.lazyAutoplay === "true") {
    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;
  }

  video.load();

  if (video.dataset.lazyAutoplay === "true") {
    video.play().catch(() => {});
  }
};

const loadLazyVideoTarget = (target) => {
  if (target.matches(".player[data-lazy-player]")) {
    void setupPlyrNodes([target]);
  }

  if (target.matches("video[data-lazy-video]")) {
    loadNativeVideo(target);
  }

  target.querySelectorAll?.(".player[data-lazy-player]").forEach((player) => {
    void setupPlyrNodes([player]);
  });

  target.querySelectorAll?.("video[data-lazy-video]").forEach((video) => {
    loadNativeVideo(video);
  });
};

const initLazyVideos = (root = document) => {
  const targets = root.querySelectorAll(".player[data-lazy-player], video[data-lazy-video]");
  if (!targets.length) {
    return;
  }

  if (typeof IntersectionObserver === "undefined") {
    targets.forEach((target) => loadLazyVideoTarget(target));
    return;
  }

  const createObserver = (rootMargin) => {
    let observer = null;
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        loadLazyVideoTarget(entry.target);
        observer.unobserve(entry.target);
      });
    }, { rootMargin });

    return observer;
  };
  const playerObserver = createObserver(LAZY_PLAYER_ROOT_MARGIN);
  const nativeVideoObserver = createObserver(LAZY_NATIVE_VIDEO_ROOT_MARGIN);

  targets.forEach((target) => {
    if (target.dataset.lazyVideoBound === "1") {
      return;
    }

    target.dataset.lazyVideoBound = "1";
    const observer = target.matches("video[data-lazy-video]") ? nativeVideoObserver : playerObserver;
    observer.observe(target.closest(".progressive-video") || target);
  });
};

const initVideoPlaceholders = () => {
  const shells = document.querySelectorAll(".progressive-video[data-video-id]");
  if (!shells.length) {
    return;
  }

  const thumbnailCache = new Map();

  const setLoaded = (shell) => {
    shell.classList.add("is-loaded");
  };

  const ensureVimeoThumbnail = async (shell) => {
    const videoId = shell.dataset.videoId;
    if (!videoId) {
      return;
    }

    if (!thumbnailCache.has(videoId)) {
      const url = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(`https://vimeo.com/${videoId}`)}`;
      thumbnailCache.set(
        videoId,
        fetch(url)
          .then((response) => {
            if (!response.ok) {
              throw new Error("Vimeo thumbnail request failed");
            }

            return response.json();
          })
          .then((data) => data?.thumbnail_url || null)
          .catch(() => null),
      );
    }

    const thumbnail = await thumbnailCache.get(videoId);
    if (thumbnail) {
      shell.style.setProperty("--placeholder-image", `url("${thumbnail}")`);
      shell.classList.add("has-placeholder");
    }
  };

  shells.forEach((shell) => {
    ensureVimeoThumbnail(shell);

    const directIframe = shell.querySelector("iframe");
    if (directIframe) {
      directIframe.addEventListener("load", () => setLoaded(shell), { once: true });
    }

    const observer = new MutationObserver(() => {
      const iframe = shell.querySelector("iframe");
      if (!iframe) {
        return;
      }

      iframe.addEventListener("load", () => setLoaded(shell), { once: true });
      observer.disconnect();
    });

    observer.observe(shell, { childList: true, subtree: true });
  });
};

const initProgressiveImages = (root = document) => {
  const images = root.querySelectorAll(".progressive-image__img");
  if (!images.length) {
    return;
  }

  const revealImage = (image) => {
    if (!(image instanceof HTMLImageElement)) {
      return;
    }

    const shell = image.closest(".progressive-image");
    image.classList.add("is-loaded");

    if (!shell || shell.classList.contains("is-complete")) {
      return;
    }

    const completeShell = () => {
      shell.classList.add("is-complete");
    };

    const onTransitionEnd = (event) => {
      if (event.target !== image || event.propertyName !== "opacity") {
        return;
      }

      image.removeEventListener("transitionend", onTransitionEnd);
      completeShell();
    };

    image.addEventListener("transitionend", onTransitionEnd);

    // Fallback for edge cases where transitionend isn't fired.
    window.setTimeout(() => {
      if (!shell.classList.contains("is-complete")) {
        image.removeEventListener("transitionend", onTransitionEnd);
        completeShell();
      }
    }, 600);
  };

  images.forEach((image) => {
    if (image.complete && image.naturalWidth > 0) {
      revealImage(image);
      return;
    }

    if (image.dataset.progressiveImageBound === "1") {
      return;
    }

    image.dataset.progressiveImageBound = "1";
    image.addEventListener("load", () => revealImage(image), { once: true });
  });
};

const initLazyImagePreloadMargin = (root = document) => {
  const images = root.querySelectorAll(".progressive-image__img[loading='lazy']");
  if (!images.length || typeof IntersectionObserver === "undefined") {
    return;
  }

  const promoteImage = (image) => {
    // Promote request before the image is actually visible.
    try {
      image.setAttribute("loading", "eager");
    } catch {
      // no-op
    }

    if ("fetchPriority" in image) {
      try {
        image.setAttribute("fetchpriority", "high");
      } catch {
        // no-op
      }
    }
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        const image = entry.target;
        promoteImage(image);
        observer.unobserve(image);
      });
    },
    { rootMargin: "1200px 0px" },
  );

  images.forEach((image) => {
    if (image.dataset.lazyPreloadBound === "1") {
      return;
    }

    image.dataset.lazyPreloadBound = "1";
    observer.observe(image);
  });
};

const initSortFilters = () => {
  const selects = document.querySelectorAll(".sort-nav .filters select");
  selects.forEach((select) => {
    select.addEventListener("change", () => {
      const sortType = select.dataset.sort;
      if (!sortType) {
        return;
      }

      const url = new URL(window.location.href);
      url.searchParams.set(sortType, select.value);
      window.location.href = url.toString();
    });
  });
};

const initScrollLinks = () => {
  document.querySelectorAll(".scroll-links a").forEach((link) => {
    link.addEventListener("click", (event) => {
      const href = link.getAttribute("href") || "";
      if (!href.includes("#")) {
        return;
      }

      event.preventDefault();
      const anchor = href.split("#")[1];
      if (!anchor) {
        return;
      }

      const target = document.querySelector(`[id="${anchor}"]`);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  });
};

const initToTop = () => {
  document.querySelectorAll(".to-top").forEach((button) => {
    button.addEventListener("click", (event) => {
      event.preventDefault();
      try {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch {
        window.scrollTo(0, 0);
      }
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });
  });
};

const initPressEntryLinkCards = () => {
  const cards = document.querySelectorAll(".press-entry-link-card[data-press-entry-url]");
  if (!cards.length) {
    return;
  }

  const openCard = (card) => {
    const url = card.dataset.pressEntryUrl;
    const target = card.dataset.pressEntryTarget || "_blank";
    if (!url) {
      return;
    }

    if (target === "_self") {
      window.location.href = url;
      return;
    }

    window.open(url, target, "noopener,noreferrer");
  };

  const isInteractiveEventTarget = (target) =>
    target instanceof Element && target.closest("a, button, input, select, textarea, label");

  cards.forEach((card) => {
    card.addEventListener("click", (event) => {
      if (isInteractiveEventTarget(event.target)) {
        return;
      }

      openCard(card);
    });

    card.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }

      if (isInteractiveEventTarget(event.target)) {
        return;
      }

      event.preventDefault();
      openCard(card);
    });
  });
};

const initMediaPopups = () => {
  const items = Array.from(document.querySelectorAll(".popup-trigger"))
    .map((trigger) => {
      const hash = trigger.getAttribute("href") || "";
      const panel = hash.startsWith("#") ? document.getElementById(hash.slice(1)) : null;

      if (!panel || !panel.classList.contains("popup-slide")) {
        return null;
      }

      return { trigger, panel, hash };
    })
    .filter(Boolean);

  if (!items.length) {
    return;
  }

  let activeIndex = -1;
  let activeItem = null;
  let placeholder = null;
  let lastFocused = null;
  let previousHtmlOverflow = "";
  let bg = null;
  let wrap = null;
  let content = null;
  let closeButton = null;
  let prevButton = null;
  let nextButton = null;
  const popupPreloads = new WeakMap();

  const createButton = (className, label, html = "") => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = className;
    button.title = label;
    button.setAttribute("aria-label", label);
    button.innerHTML = html;
    return button;
  };

  const primePopupImages = (panel) => {
    const images = panel.querySelectorAll(".progressive-image__img");
    if (!images.length) {
      return;
    }

    const preloads = [];

    images.forEach((image) => {
      if (!(image instanceof HTMLImageElement)) {
        return;
      }

      if (image.dataset.sizes) {
        image.sizes = image.dataset.sizes;
      }

      image.loading = "eager";
      image.setAttribute("fetchpriority", "high");

      if (image.complete && image.naturalWidth > 0) {
        return;
      }

      const preload = new Image();
      preload.decoding = "async";
      preload.sizes = image.sizes;
      preload.srcset = image.srcset;
      preload.src = image.currentSrc || image.src;

      if ("fetchPriority" in preload) {
        preload.fetchPriority = "high";
      }

      preloads.push(preload);
    });

    if (preloads.length) {
      popupPreloads.set(panel, preloads);
    }

    initProgressiveImages(panel);
  };

  const buildShell = () => {
    if (wrap) {
      return;
    }

    bg = document.createElement("div");
    bg.className = "mfp-bg mfp-ready";

    wrap = document.createElement("div");
    wrap.className = "mfp-wrap mfp-close-btn-in mfp-align-top mfp-auto-cursor mfp-ready";
    wrap.tabIndex = -1;
    wrap.style.overflow = "hidden auto";

    const container = document.createElement("div");
    container.className = "mfp-container mfp-inline-holder";

    content = document.createElement("div");
    content.className = "mfp-content";

    closeButton = createButton("mfp-close mfp-prevent-close", "Close", "&times;");
    prevButton = createButton("mfp-arrow mfp-arrow-left mfp-prevent-close", "Previous");
    nextButton = createButton("mfp-arrow mfp-arrow-right mfp-prevent-close", "Next");

    container.append(content, prevButton, nextButton);
    wrap.append(container);

    bg.addEventListener("click", () => closePopup());
    wrap.addEventListener("click", (event) => {
      if (event.target.closest(".mfp-prevent-close")) {
        return;
      }

      closePopup();
    });
    closeButton.addEventListener("click", () => closePopup());
    prevButton.addEventListener("click", () => goToPopup(activeIndex - 1));
    nextButton.addEventListener("click", () => goToPopup(activeIndex + 1));
  };

  const updateLocationHash = (hash) => {
    if (!window.history || window.location.hash === hash) {
      return;
    }

    window.history.pushState(null, "", hash);
  };

  const clearLocationHash = (hash) => {
    if (!window.history || window.location.hash !== hash) {
      return;
    }

    window.history.pushState(null, "", `${window.location.pathname}${window.location.search}`);
  };

  const restoreActivePanel = () => {
    if (!activeItem || !placeholder) {
      return;
    }

    closeButton.remove();
    activeItem.panel.classList.add("mfp-hide");
    placeholder.parentNode.insertBefore(activeItem.panel, placeholder);
    placeholder.remove();
    placeholder = null;
  };

  const openPopup = (index, shouldUpdateHash = true) => {
    const normalizedIndex = (index + items.length) % items.length;
    const item = items[normalizedIndex];
    const wasClosed = activeIndex < 0;

    buildShell();
    restoreActivePanel();

    if (wasClosed) {
      lastFocused = document.activeElement;
      previousHtmlOverflow = document.documentElement.style.overflow;
    }

    activeIndex = normalizedIndex;
    activeItem = item;
    placeholder = document.createComment("popup-placeholder");

    item.panel.parentNode.insertBefore(placeholder, item.panel);
    item.panel.classList.remove("mfp-hide");
    item.panel.append(closeButton);
    primePopupImages(item.panel);
    content.replaceChildren(item.panel);

    if (!bg.parentNode) {
      document.body.append(bg, wrap);
    }

    prevButton.hidden = items.length < 2;
    nextButton.hidden = items.length < 2;
    document.documentElement.style.overflow = "hidden";
    closeButton.focus({ preventScroll: true });

    if (shouldUpdateHash) {
      updateLocationHash(item.hash);
    }
  };

  const closePopup = (shouldClearHash = true) => {
    if (activeIndex < 0 || !activeItem) {
      return;
    }

    const closingHash = activeItem.hash;
    restoreActivePanel();
    bg.remove();
    wrap.remove();
    document.documentElement.style.overflow = previousHtmlOverflow;
    activeIndex = -1;
    activeItem = null;

    if (shouldClearHash) {
      clearLocationHash(closingHash);
    }

    if (lastFocused && typeof lastFocused.focus === "function") {
      lastFocused.focus({ preventScroll: true });
    }
  };

  function goToPopup(index) {
    if (activeIndex < 0) {
      return;
    }

    openPopup(index);
  }

  const syncFromHash = () => {
    const index = items.findIndex((item) => item.hash === window.location.hash);

    if (index >= 0) {
      openPopup(index, false);
    } else if (activeIndex >= 0) {
      closePopup(false);
    }
  };

  items.forEach((item, index) => {
    const prewarm = () => primePopupImages(item.panel);

    item.trigger.addEventListener("pointerenter", prewarm, { once: true });
    item.trigger.addEventListener("focus", prewarm, { once: true });
    item.trigger.addEventListener("touchstart", prewarm, { once: true, passive: true });
    item.trigger.addEventListener("click", (event) => {
      event.preventDefault();
      openPopup(index);
    });
  });

  document.addEventListener("keydown", (event) => {
    if (activeIndex < 0) {
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      closePopup();
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      goToPopup(activeIndex - 1);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      goToPopup(activeIndex + 1);
    }
  });

  window.addEventListener("hashchange", syncFromHash);
  window.addEventListener("popstate", syncFromHash);
  syncFromHash();
};

const typeOrder = ["project", "expertise-industry", "expertise-service", "team", "unknown"];
const typeLabels = {
  project: "Projects",
  "expertise-industry": "Expertise",
  "expertise-service": "Expertise",
  team: "Team",
  unknown: "Other",
};
const typeSingularLabels = {
  project: "Project",
  "expertise-industry": "Expertise",
  "expertise-service": "Service",
  team: "Team",
  unknown: "Result",
};

const renderSearchTile = (type, item) => {
  if (!item || !item.url || !item.title) {
    return "";
  }

  const href = escapeHtml(item.url);
  const title = escapeHtml(item.title);
  const summary = escapeHtml(item.summary || "");
  const thumbnailUrl = item.thumbnailUrl ? escapeHtml(item.thumbnailUrl) : "";
  const focalPoint = normalizeFocalPoint(item.thumbnailFocalPoint);
  const focalStyle = escapeHtml(`object-position:${focalPoint};--focal-point:${focalPoint};`);
  const singularLabel = escapeHtml(typeSingularLabels[type] || "Result");
  const imageHtml = thumbnailUrl
    ? `<div class="img-wrapper"><img class="thumbnail" src="${thumbnailUrl}" style="${focalStyle}" alt="${title}" loading="lazy" decoding="async"></div>`
    : `<div class="thumbnail"></div>`;
  let bodyHtml = "";

  if (type === "project") {
    const clientName = Array.isArray(item.clientNames) && item.clientNames.length
      ? escapeHtml(item.clientNames[0])
      : "";

    bodyHtml += imageHtml;
    if (clientName) {
      bodyHtml += `<h3>${clientName}</h3>`;
      bodyHtml += `<p>${title}</p>`;
    } else {
      bodyHtml += `<h3>${title}</h3>`;
      if (summary) {
        bodyHtml += `<p class="search-result-summary">${summary}</p>`;
      }
    }
  } else if (type === "team") {
    bodyHtml += imageHtml;
    bodyHtml += `<h3>${title}</h3>`;
    if (summary) {
      bodyHtml += `<p class="search-result-summary">${summary}</p>`;
    }
  } else {
    bodyHtml += imageHtml;
    bodyHtml += `<h3>${title}</h3>`;
    bodyHtml += `<p class="search-result-type">${singularLabel}</p>`;
    if (summary) {
      bodyHtml += `<p class="search-result-summary">${summary}</p>`;
    }
  }

  return `<a href="${href}" class="tile search-result-tile search-result-tile--${escapeHtml(type)}" data-content-type="${escapeHtml(type)}">${bodyHtml}</a>`;
};

const buildSearchResultsHtml = (data, query) => {
  const groups = data.groups || {};
  const groupCounts = {};
  (data.groupCounts || []).forEach((bucket) => {
    groupCounts[bucket.key] = bucket.doc_count;
  });

  const total = data.total || 0;
  if (!total) {
    return {
      total,
      html: `<div class="page-title"><h2>Nothing found for <span class="extended">${escapeHtml(query.toLowerCase())}</span><br/>May we suggest our <a href="/work">work</a>?</h2></div>`,
    };
  }

  let html = "";
  const configuredOrder = Array.isArray(data.groupOrder) ? data.groupOrder : [];
  const discoveredTypes = Object.keys(groups);
  const orderedTypes = [
    ...configuredOrder.filter((type) => discoveredTypes.includes(type)),
    ...discoveredTypes.filter((type) => !configuredOrder.includes(type)),
  ];
  const renderOrder = orderedTypes.length ? orderedTypes : typeOrder;

  renderOrder.forEach((type) => {
    const items = groups[type] || [];
    if (!items.length) {
      return;
    }

    const label = typeLabels[type] || type;
    const count = groupCounts[type] || items.length;
    html += `<section class="search-group search-group--${escapeHtml(type)}">`;
    html += `<h3 class="section-header">${escapeHtml(label)} <span class="counter">${count}</span></h3>`;
    html += `<div class="grid set search-results-set">`;

    items.forEach((item) => {
      html += renderSearchTile(type, item);
    });

    html += `</div></section>`;
  });

  return { total, html };
};

const fetchSearchResults = async (query, endpoint, signal) => {
  const response = await fetch(`${endpoint}?q=${encodeURIComponent(query)}&perPage=80`, { signal });
  if (!response.ok) {
    throw new Error("Search request failed");
  }

  const data = await response.json();
  return buildSearchResultsHtml(data, query);
};

const initSearchResults = () => {
  const app = document.getElementById("search-results-app");
  const countNode = document.getElementById("search-total-count");
  if (!app) {
    return;
  }

  const query = (app.dataset.query || "").trim();
  const endpoint = app.dataset.endpoint || "/actions/span-search/query";
  if (!query) {
    return;
  }

  fetchSearchResults(query, endpoint)
    .then((result) => {
      if (countNode) {
        countNode.textContent = `${result.total}`;
      }
      app.innerHTML = result.html;
    })
    .catch(() => {
      app.innerHTML = '<div class="search-error">Search is temporarily unavailable. Please try again.</div>';
    });
};

const initInfiniteScroll = () => {
  const container = document.querySelector(".projects-set");
  const status = document.querySelector(".page-load-status");
  const pagination = document.querySelector(".pagination");
  let nextLink = pagination?.querySelector(".pagination-next");

  if (!container || !nextLink) {
    return;
  }

  let loading = false;
  const target = status || container;
  const setLoadingState = (isLoading) => {
    if (status) {
      status.classList.toggle("is-loading", isLoading);
    }
  };

  const loadNextPage = async () => {
    if (loading || !nextLink) {
      return;
    }

    const nextUrl = nextLink.getAttribute("href");
    if (!nextUrl) {
      return;
    }

    loading = true;
    setLoadingState(true);

    try {
      const response = await fetch(nextUrl, { headers: { "X-Requested-With": "XMLHttpRequest" } });
      if (!response.ok) {
        throw new Error("Infinite scroll request failed");
      }

      const markup = await response.text();
      const doc = new DOMParser().parseFromString(markup, "text/html");
      const newTiles = doc.querySelectorAll(".projects-set .tile");

      const appendedTiles = [];
      const insertBefore = status?.parentElement === container ? status : null;
      newTiles.forEach((tile) => {
        container.insertBefore(tile, insertBefore);
        appendedTiles.push(tile);
      });

      appendedTiles.forEach((tile) => {
        initProgressiveImages(tile);
        initLazyImagePreloadMargin(tile);
      });

      const updatedPagination = doc.querySelector(".pagination");
      if (pagination && updatedPagination) {
        pagination.innerHTML = updatedPagination.innerHTML;
      }

      nextLink = pagination?.querySelector(".pagination-next") || null;
      if (!nextLink && status) {
        status.style.display = "none";
      }
    } catch {
      // Keep current state on error; user can still paginate manually if link remains.
    } finally {
      loading = false;
      setLoadingState(false);
    }
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          loadNextPage();
        }
      });
    },
    { rootMargin: "250px 0px" },
  );

  observer.observe(target);
};

const boot = () => {
  initProgressiveImages();
  initLazyImagePreloadMargin();
  initVideoPlaceholders();
  initLazyVideos();
  void initPlayers();
  initSortFilters();
  initScrollLinks();
  initToTop();
  initPressEntryLinkCards();
  initMediaPopups();
  initSearchResults();
  initInfiniteScroll();
};

window.Alpine = Alpine;
Alpine.start();

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot, { once: true });
} else {
  boot();
}
